import { Injectable } from '@angular/core';
import { Observable, throwError, of, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthenticationService } from '../authentication/authentication.service';
import { HttpService } from '../http/http.service';

interface UserConfigRecord {
  userid: string;
  ignored_domains?: string[] | null;
  bc_ignore_default_domain?: boolean;
  bc_domain?: string[] | null;
  push_mode?: string | null;
  auto_sync?: boolean | null;
  notetaker_mode?: boolean | null;
}

interface CrmConnectionRecord {
  provider?: string | null;
}

interface CrmConnectResponse {
  auth_url?: string | null;
}

interface CrmActionResponse {
  success?: boolean;
  message?: string;
  data?: string;
}

export type CrmProvider = 'salesforce' | 'zoho' | 'hubspot' | 'pipedrive';

@Injectable({
  providedIn: 'root',
})
export class UserConfigService {
  private readonly _basePath = 'rest/v1/user_config';
  private readonly _crmConnectionsPath = 'rest/v1/crm_connections';
  private readonly _crmApiBase = 'https://crm.convverse.ai/crm';
  private readonly _allowedCrmProviders: ReadonlySet<string> = new Set(['salesforce', 'zoho', 'hubspot', 'pipedrive']);

  constructor(
    private readonly _auth: AuthenticationService,
    private readonly _http: HttpService
  ) {}

  loadIgnoredDomains(): Observable<string[]> {
    const userId = this._getUserId();
    if (!userId) {
      return throwError(() => new Error('No active session'));
    }

    const url = `${this._basePath}?select=ignored_domains&userid=eq.${userId}&limit=1`;
    return this._http.get(url, true).pipe(
      map((response: UserConfigRecord[] | UserConfigRecord | null) => {
        if (!response) {
          return [];
        }

        const record = Array.isArray(response) ? response[0] : response;
        const rawDomains = record?.ignored_domains ?? [];
        return rawDomains
          .filter((domain): domain is string => typeof domain === 'string' && domain.trim().length > 0)
          .map(domain => domain.toLowerCase());
      })
    );
  }

  saveIgnoredDomains(domains: string[]): Observable<void> {
    const userId = this._getUserId();
    if (!userId) {
      return throwError(() => new Error('No active session'));
    }

    const distinctDomains = Array.from(new Set(domains.map(domain => domain.toLowerCase())));
    const payload = {
      userid: userId,
      ignored_domains: distinctDomains,
    };

    const url = `${this._basePath}?on_conflict=userid`;

    return this._http
      .post(url, payload, true, {
        headers: {
          Prefer: 'resolution=merge-duplicates,return=minimal',
        },
      })
      .pipe(map(() => void 0));
  }

  loadBotSchedulingStatus(): Observable<boolean> {
    const userId = this._getUserId();
    if (!userId) {
      return of(false); // default to false if not logged in
    }

    const url = `rest/v1/user_access_controls?select=disable_scheduling&user_id=eq.${userId}&limit=1`;
    return this._http.get(url, true).pipe(
      map((response: any) => {
        if (!response || (Array.isArray(response) && response.length === 0)) {
          return false; // Default is scheduling ON, which means disable_scheduling is false
        }
        const record = Array.isArray(response) ? response[0] : response;
        return record?.disable_scheduling ?? false;
      })
    );
  }

  toggleBotScheduling(disable: boolean): Observable<void> {
    const url = `rest/v1/rpc/user_toggle_scheduling`;
    return this._http.post(url, { p_disable: disable }, true).pipe(
      map(() => void 0)
    );
  }

  getUserDefaultDomain(): string | null {
    const userDetail = this._auth.getUserDetail();
    const email = userDetail?.user?.email || (userDetail as any)?.email;
    if (!email) {
      return null;
    }

    const parts = email.split('@');
    if (parts.length !== 2) {
      return null;
    }

    return parts[1].toLowerCase();
  }

  async loadBattleCardDomains(): Promise<{ ignoreDefault: boolean; domains: string[] }> {
    const userId = this._getUserId();
    if (!userId) {
      return { ignoreDefault: false, domains: [] };
    }

    try {
      const url = `${this._basePath}?select=bc_ignore_default_domain,bc_domain&userid=eq.${userId}&limit=1`;
      const response = await firstValueFrom(this._http.get(url, true));
      
      if (!response) {
        return { ignoreDefault: false, domains: [] };
      }

      const record = Array.isArray(response) ? response[0] : response;
      const rawDomains = record?.bc_domain ?? [];
      const domains = rawDomains
        .filter((domain: any): domain is string => typeof domain === 'string' && domain.trim().length > 0)
        .map((domain: string) => domain.toLowerCase());

      return {
        ignoreDefault: record?.bc_ignore_default_domain ?? false,
        domains
      };
    } catch (error) {
      console.error('Error loading battle card domains:', error);
      return { ignoreDefault: false, domains: [] };
    }
  }

  async saveBattleCardDomains(ignoreDefault: boolean, domains: string[]): Promise<void> {
    const userId = this._getUserId();
    if (!userId) {
      throw new Error('No active session');
    }

    const distinctDomains = Array.from(new Set(domains.map(domain => domain.toLowerCase())));
    const payload = {
      userid: userId,
      bc_ignore_default_domain: ignoreDefault,
      bc_domain: distinctDomains,
    };

    const url = `${this._basePath}?on_conflict=userid`;

    await firstValueFrom(
      this._http.post(url, payload, true, {
        headers: {
          Prefer: 'resolution=merge-duplicates,return=minimal',
        },
      })
    );
  }

  async loadCrmSyncConfig(): Promise<{ pushMode: 'notes' | 'tasks'; autoSync: boolean }> {
    const userId = this._getUserId();
    if (!userId) {
      return { pushMode: 'notes', autoSync: false };
    }

    try {
      const url = `${this._basePath}?select=push_mode,auto_sync&userid=eq.${userId}&limit=1`;
      const response = await firstValueFrom(this._http.get(url, true));

      if (!response) {
        return { pushMode: 'notes', autoSync: false };
      }

      const record = Array.isArray(response) ? response[0] : response;
      const rawPushMode = (record?.push_mode ?? 'notes').toString().toLowerCase();
      const pushMode: 'notes' | 'tasks' = rawPushMode === 'tasks' ? 'tasks' : 'notes';

      return {
        pushMode,
        autoSync: Boolean(record?.auto_sync ?? false),
      };
    } catch (error) {
      console.error('Error loading CRM sync config:', error);
      return { pushMode: 'notes', autoSync: false };
    }
  }

  async saveCrmSyncConfig(pushMode: 'notes' | 'tasks', autoSync: boolean): Promise<void> {
    const userId = this._getUserId();
    if (!userId) {
      throw new Error('No active session');
    }

    const payload = {
      userid: userId,
      push_mode: pushMode,
      auto_sync: autoSync,
    };

    const url = `${this._basePath}?on_conflict=userid`;

    await firstValueFrom(
      this._http.post(url, payload, true, {
        headers: {
          Prefer: 'resolution=merge-duplicates,return=minimal',
        },
      })
    );
  }

  async loadNotetakerMode(): Promise<boolean> {
    const userId = this._getUserId();
    if (!userId) {
      return false;
    }

    try {
      const url = `${this._basePath}?select=notetaker_mode&userid=eq.${userId}&limit=1`;
      const response = await firstValueFrom(this._http.get(url, true));

      if (!response) {
        return false;
      }

      const record = Array.isArray(response) ? response[0] : response;
      return Boolean(record?.notetaker_mode ?? false);
    } catch (error) {
      console.error('Error loading notetaker mode:', error);
      return false;
    }
  }

  async saveNotetakerMode(enabled: boolean): Promise<void> {
    const userId = this._getUserId();
    if (!userId) {
      throw new Error('No active session');
    }

    const payload = {
      userid: userId,
      notetaker_mode: enabled,
    };

    const url = `${this._basePath}?on_conflict=userid`;

    await firstValueFrom(
      this._http.post(url, payload, true, {
        headers: {
          Prefer: 'resolution=merge-duplicates,return=minimal',
        },
      })
    );
  }

  async loadConnectedCrmProvider(): Promise<CrmProvider | null> {
    const userId = this._getUserId();
    if (!userId) {
      return null;
    }

    try {
      const url = `${this._crmConnectionsPath}?select=provider&user_id=eq.${userId}&order=updated_at.desc&limit=1`;
      const response = await firstValueFrom(this._http.get(url, true));

      if (!response) {
        return null;
      }

      const record = (Array.isArray(response) ? response[0] : response) as CrmConnectionRecord;
      const provider = record?.provider?.toString().toLowerCase() ?? '';

      if (!this._allowedCrmProviders.has(provider)) {
        return null;
      }

      return provider as CrmProvider;
    } catch (error) {
      console.error('Error loading CRM connection:', error);
      return null;
    }
  }

  async requestCrmConnectUrl(provider: CrmProvider): Promise<string> {
    const response = await this._callCrmAuthEndpoint<CrmConnectResponse>(provider, 'connect');
    const authUrl = response?.auth_url?.trim();
    if (!authUrl) {
      throw new Error('CRM connect URL not found in response');
    }

    return authUrl;
  }

  async disconnectCrmProvider(provider: CrmProvider): Promise<void> {
    const response = await this._callCrmAuthEndpoint<CrmActionResponse>(provider, 'disconnect');
    if (!response?.success) {
      throw new Error(response?.message || `Failed to disconnect ${provider}`);
    }
  }

  private async _callCrmAuthEndpoint<T = void>(provider: CrmProvider, action: 'connect' | 'disconnect'): Promise<T> {
    const authToken = this._auth.getAuthToken;
    if (!authToken) {
      throw new Error('No active session');
    }

    const response = await fetch(`${this._crmApiBase}/${provider}/auth/${action}`, {
      method: action === 'disconnect' ? 'POST' : 'GET',
      headers: {
        Authorization: authToken,
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`CRM ${action} failed with status ${response.status}`);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  private _getUserId(): string | null {
    const userDetail = this._auth.getUserDetail();
    return userDetail?.user?.id || (userDetail as any)?.id || null;
  }
}
